import type { AlvoDiagnostico } from '@baiocchi/shared'
import { CabecalhoCartao, Cartao, Esqueleto, EstadoVazio, Etiqueta } from '@baiocchi/ui'
import { useQueries } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { AvisoDefasagem, BadgeScore, formatarMetrica } from './componentes'
import { FiltrosMetaAds, parametrosDoFiltro } from './filtros'

/**
 * TELA-META-06 — Diagnóstico (§8, §9, §11).
 *
 * Quatro listas, e a ordem é a de urgência para quem paga a conta:
 *
 * 1. **Candidatos a pausa** — dinheiro saindo sem retorno. É a única lista que muda a
 *    fatura hoje.
 * 2. **Alertas** — algo piorou e vale olhar.
 * 3. **Possível fadiga** — o criativo pode estar cansando; exige produção, não clique.
 * 4. **Oportunidades** — onde colocar mais dinheiro.
 *
 * Oportunidade por último de propósito: ela é a mais agradável de ler e a menos urgente, e
 * colocá-la no topo faria o gestor sair da tela satisfeito sem ver o anúncio que queimou
 * R$ 400 sem lead.
 */
export function PaginaMetaAdsDiagnostico() {
  const { filtros, definir } = useFiltrosUrl()
  const parametros = parametrosDoFiltro(filtros)

  const [contas, consulta] = useQueries({
    queries: [
      {
        queryKey: chaves.metaAds.contas,
        queryFn: ({ signal }: { signal: AbortSignal }) => metaAdsApi.contas(signal),
        staleTime: 300_000,
      },
      {
        queryKey: chaves.metaAds.diagnostico(parametros),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          metaAdsApi.diagnostico(parametros, signal),
        staleTime: 60_000,
      },
    ],
  })

  const dados = consulta.data?.dados

  return (
    <>
      <CabecalhoPagina
        titulo="Diagnóstico"
        descricao="O que mudou, o que está caro e o que pode estar cansando."
      />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      {consulta.isLoading ? <Esqueleto className="h-64 w-full" /> : null}

      {dados !== undefined ? (
        dados.semDados ? (
          <Cartao>
            <EstadoVazio
              titulo="Sem dados para diagnosticar"
              descricao="Nenhuma métrica sincronizada neste recorte. Ajuste o período ou confira a sincronização em Contas."
            />
          </Cartao>
        ) : (
          <div className="space-y-4">
            <Grupo
              titulo="Candidatos a pausa"
              descricao="Gasto sem retorno ou custo por lead muito acima do resto da conta. Pausar libera o orçamento — nada é pausado automaticamente."
              alvos={dados.candidatosPausa}
              vazio="Nenhum anúncio em situação de pausa. É a lista que se quer vazia."
            />
            <Grupo
              titulo="Alertas"
              descricao="Piora relevante no período: custo subindo, cliques caindo, entrega interrompida."
              alvos={dados.alertas}
              vazio="Nenhum alerta no período."
            />
            <Grupo
              titulo="Possível fadiga de criativo"
              descricao="Frequência alta com queda de cliques e alta de custo. É indicação, não certeza — sazonalidade e leilão mais caro produzem o mesmo padrão."
              alvos={dados.possivelFadiga}
              vazio="Nenhum sinal de desgaste de criativo."
            />
            <Grupo
              titulo="Oportunidades"
              descricao="Desempenho bem acima da mediana da conta — onde vale considerar aumento gradual de orçamento."
              alvos={dados.oportunidades}
              vazio="Nenhum destaque positivo com volume suficiente no período."
            />
          </div>
        )
      ) : null}

      <div className="mt-4">
        <AvisoDefasagem
          defasagemMinutos={consulta.data?.meta.defasagemMinutos ?? 0}
          atualizadoEm={consulta.data?.meta.atualizadoEm}
        />
      </div>
    </>
  )
}

function Grupo({
  titulo,
  descricao,
  alvos,
  vazio,
}: {
  readonly titulo: string
  readonly descricao: string
  readonly alvos: ReadonlyArray<AlvoDiagnostico>
  readonly vazio: string
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={
          <span className="flex items-center gap-2">
            {titulo}
            <Etiqueta tom="neutro" tamanho="sm">
              {alvos.length}
            </Etiqueta>
          </span>
        }
        descricao={descricao}
      />
      <div className="p-4 sm:p-5">
        {alvos.length === 0 ? (
          <p className="text-sm text-conteudo-suave">{vazio}</p>
        ) : (
          <ul className="space-y-3">
            {alvos.map((alvo) => (
              <li
                key={`${alvo.nivel}:${alvo.idExterno}`}
                className="border-b border-borda pb-3 last:border-0 last:pb-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-conteudo">{alvo.nome}</p>
                    <p className="text-xs text-conteudo-fraco">
                      {rotularNivel(alvo.nivel)} · {alvo.contaNome}
                    </p>
                  </div>
                  <BadgeScore score={alvo.score} />
                </div>

                {/*
                  As anomalias vêm com o número dentro da frase — "CPL subiu 47% na segunda
                  metade do período". É a diferença entre um alerta acionável e um adjetivo.
                */}
                <ul className="mt-2 space-y-1">
                  {alvo.anomalias.map((anomalia, indice) => (
                    <li
                      key={`${anomalia.tipo}-${indice}`}
                      className="flex items-start gap-2 text-sm"
                    >
                      <Etiqueta tom={TOM_SEVERIDADE[anomalia.severidade]} tamanho="sm">
                        {ROTULO_SEVERIDADE[anomalia.severidade]}
                      </Etiqueta>
                      <span className="text-conteudo-suave">{anomalia.mensagem}</span>
                    </li>
                  ))}
                </ul>

                {alvo.fadiga.grau !== 'SEM_SINAL' ? (
                  <p className="mt-1 text-sm text-conteudo-suave">{alvo.fadiga.resumo}</p>
                ) : null}

                <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums text-conteudo-fraco">
                  <span>Investimento {formatarMetrica(alvo.metricas.gasto, 'MOEDA')}</span>
                  <span>{alvo.metricas.leads} leads</span>
                  <span>CPL {formatarMetrica(alvo.metricas.cpl, 'MOEDA')}</span>
                  <span>CTR {formatarMetrica(alvo.metricas.ctr, 'PERCENTUAL')}</span>
                  <span>Frequência {formatarMetrica(alvo.metricas.frequencia, 'DECIMAL')}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  )
}

const TOM_SEVERIDADE = {
  CRITICO: 'perigo',
  ATENCAO: 'atencao',
  OPORTUNIDADE: 'sucesso',
  INFORMATIVO: 'info',
} as const

const ROTULO_SEVERIDADE = {
  CRITICO: 'Crítico',
  ATENCAO: 'Atenção',
  OPORTUNIDADE: 'Oportunidade',
  INFORMATIVO: 'Informativo',
} as const

function rotularNivel(nivel: AlvoDiagnostico['nivel']): string {
  switch (nivel) {
    case 'CAMPANHA':
      return 'Campanha'
    case 'CONJUNTO':
      return 'Conjunto'
    case 'ANUNCIO':
      return 'Anúncio'
    default:
      return 'Conta'
  }
}
